import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { submitFeedback, type FeedbackType } from './submitFeedback';

interface FeedbackSheetProps {
  visible: boolean;
  currentScreen?: string;
  onClose: () => void;
}

export const FeedbackSheet: React.FC<FeedbackSheetProps> = ({ visible, currentScreen, onClose }) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);

  const OPTIONS = [
    {
      key: 'bug' as FeedbackType,
      icon: 'alert-circle' as const,
      label: t('feedback.bug.label'),
      description: t('feedback.bug.description'),
      placeholder: t('feedback.bug.placeholder'),
    },
    {
      key: 'feature' as FeedbackType,
      icon: 'star' as const,
      label: t('feedback.feature.label'),
      description: t('feedback.feature.description'),
      placeholder: t('feedback.feature.placeholder'),
    },
    {
      key: 'impression' as FeedbackType,
      icon: 'message-circle' as const,
      label: t('feedback.impression.label'),
      description: t('feedback.impression.description'),
      placeholder: t('feedback.impression.placeholder'),
    },
  ];

  const selectedOption = OPTIONS.find(o => o.key === selectedType);

  function reset() {
    setSelectedType(null);
    setText('');
    setSubmitting(false);
    setSubmitted(false);
    setFailed(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    if (!text.trim() || !selectedType || submitting) return;
    setSubmitting(true);
    setFailed(false);

    try {
      await submitFeedback({
        type: selectedType,
        description: text,
        currentScreen,
      });
    } catch (error) {
      console.warn('[Feedback] Submission failed, keeping the sheet open:', error);
      setSubmitting(false);
      // Deliberately leaves `text` intact so the user can retry without
      // retyping — never clear the draft on a failed send.
      setFailed(true);
      return;
    }

    setSubmitting(false);
    setSubmitted(true);
    setTimeout(handleClose, 1500);
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.overlay}>
          <TouchableOpacity style={StyleSheet.absoluteFill} onPress={handleClose} activeOpacity={1} />
          <View style={styles.sheet}>
            <View style={styles.handle} />
            <ScrollView
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              bounces={false}
            >
              {submitted ? (
                <View style={styles.successContainer}>
                  <Feather name="check-circle" size={32} color={Paprika.success} />
                  <Text style={styles.successText}>{t('feedback.success')}</Text>
                </View>
              ) : selectedType === null ? (
                <>
                  <Text style={styles.title}>{t('feedback.sheetTitle')}</Text>
                  <Text style={styles.subtitle}>{t('feedback.sheetSubtitle')}</Text>
                  {OPTIONS.map((option, i) => (
                    <TouchableOpacity
                      key={option.key}
                      style={[styles.optionRow, i < OPTIONS.length - 1 && styles.optionBorder]}
                      onPress={() => setSelectedType(option.key)}
                      activeOpacity={0.7}
                    >
                      <View style={styles.optionIcon}>
                        <Feather name={option.icon} size={18} color={Paprika.accent} />
                      </View>
                      <View style={styles.optionText}>
                        <Text style={styles.optionLabel}>{option.label}</Text>
                        <Text style={styles.optionDescription}>{option.description}</Text>
                      </View>
                      <Feather name="chevron-right" size={16} color={Paprika.textMuted} />
                    </TouchableOpacity>
                  ))}
                </>
              ) : (
                <>
                  <TouchableOpacity
                    style={styles.backRow}
                    onPress={() => {
                      setSelectedType(null);
                      setFailed(false);
                    }}
                  >
                    <Feather name="arrow-left" size={16} color={Paprika.textSecondary} />
                    <Text style={styles.backLabel}>{selectedOption?.label}</Text>
                  </TouchableOpacity>
                  <TextInput
                    style={styles.textInput}
                    placeholder={selectedOption?.placeholder}
                    placeholderTextColor={Paprika.textMuted}
                    value={text}
                    onChangeText={value => {
                      setText(value);
                      if (failed) setFailed(false);
                    }}
                    multiline
                    autoFocus
                    textAlignVertical="top"
                  />
                  {failed && (
                    <View style={styles.errorRow}>
                      <Feather name="alert-triangle" size={16} color={Paprika.error} />
                      <Text style={styles.errorText}>{t('feedback.error')}</Text>
                    </View>
                  )}
                  <TouchableOpacity
                    style={[styles.submitButton, (!text.trim() || submitting) && styles.submitDisabled]}
                    onPress={handleSubmit}
                    disabled={!text.trim() || submitting}
                    activeOpacity={0.8}
                  >
                    {submitting ? (
                      <ActivityIndicator color={Paprika.onPrimary} size="small" />
                    ) : (
                      <Text style={styles.submitLabel}>
                        {t(failed ? 'feedback.retry' : 'feedback.send')}
                      </Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  sheet: {
    backgroundColor: Paprika.surfaceLowest,
    borderTopLeftRadius: PaprikaRadius.lg,
    borderTopRightRadius: PaprikaRadius.lg,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: Platform.OS === 'ios' ? 40 : PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.sm,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: Paprika.border,
    alignSelf: 'center',
    marginBottom: PaprikaSpacing.lg,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  subtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.lg,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    gap: PaprikaSpacing.md,
  },
  optionBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  optionIcon: {
    width: 36,
    height: 36,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.accentLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  optionText: {
    flex: 1,
  },
  optionLabel: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  optionDescription: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
  },
  backLabel: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textSecondary,
  },
  textInput: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    minHeight: 120,
    marginBottom: PaprikaSpacing.md,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
  },
  errorText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.error,
    flex: 1,
  },
  submitButton: {
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.xl,
    paddingVertical: PaprikaSpacing.md,
    alignItems: 'center',
  },
  submitDisabled: {
    opacity: 0.4,
  },
  submitLabel: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.onPrimary,
  },
  successContainer: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.xl,
    gap: PaprikaSpacing.md,
  },
  successText: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.textPrimary,
  },
});
